import React, { useState, useEffect } from "react";
import { APP_MESSAGES, DATE_FORMATS, REPORT_STATUS } from "src/shared/config/src";
import { ChildrenStoreData, TransitionReportResponseType } from "src/shared/types/src";
import { useAppSelector } from "../../services/store";
import { getCurrentChild } from "src/shared/redux-state/src";
import { getTransitionReportAPI, signTransitionReport } from "src/shared/services/src";
import { AxiosError } from "axios";
import { dateFormatter } from "src/shared/utils/src";
import LegendsELG from "./components/LegendsELG";
import COEL from "./components/COEL";
import ReportFields from "./components/ReportFields";
import { isArray } from "lodash";
import { BaseCard, CustomButton, CustomCheckbox, TransitionReportLoader } from "src/app/components";
import { useNavigate, useLocation } from "react-router-dom";
import ProfileLayout from "src/app/layouts/ProfileLayout";
import { toast } from "react-toastify";
import { IoCaretDownSharp, IoCaretForwardSharp } from "react-icons/io5";
import { MdCheck } from "react-icons/md";
import { navigateToDownloadAppScreen } from "src/app/utilities/helpers";
import TextRenderer from "src/app/components/commonComponents/TextRenderer";

const TransitionReport = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { reportID } = location?.state as Readonly<{ reportID: string }>;

  const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);

  const [isVisible, setIsVisible] = useState<Array<string>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [reportDetails, setReportDetails] = useState<TransitionReportResponseType>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [keepCopy, setKeepCopy] = useState<boolean>(false);
  const [shareWithOther, setShareWithOther] = useState<boolean>(false);
  const [parentComments, setParentComments] = useState<string>('');
  const [acknowledged, setIsAcknowledged] = useState<boolean>(false);

  useEffect(() => {
    init()
  }, [reportID]);

  const init = async () => {
    try {
      setLoading(true);

      const response = await getTransitionReportAPI({
        child: currentChild,
        reportID: reportID
      }) as TransitionReportResponseType

      if (response && Object.keys(response).length > 0) {
        setReportDetails(response)
      } else {
        setReportDetails({} as TransitionReportResponseType);
      }

    } catch (error) {
      if (error instanceof AxiosError) {
        const { errors } = error.response?.data;
        if (errors && Object.keys(errors).length > 0) {
          toast.error(errors.join(", "), { autoClose: 5000 });
        } else {
          toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
        }
      } else {
        toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
      }
    } finally {
      setLoading(false)
    }
  }

  const onPressComplete = async () => {
    if (!acknowledged) {
      return toast.error('Please tick the acknowledgement checkbox', { autoClose: 5000 });
    };

    setSubmitting(true);

    try {
      const response = await signTransitionReport({
        child: currentChild,
        reportID: reportID,
        params: {
          comment: parentComments,
          keepCopy: keepCopy,
          shareWithOther: shareWithOther
        }
      });

      if (response) {
        toast.success('Report submitted successfully');

        setTimeout(() => {
          navigate(-1);
        }, 100);
      } else {
        toast.error('Could not submit report. Please try again after some time.', { autoClose: 5000 });
      }

    } catch (error) {
      if (error instanceof AxiosError) {
        const { errors } = error.response?.data;
        if (errors && Object.keys(errors).length > 0) {
          toast.error(errors.join(", "), { autoClose: 5000 });
        } else {
          toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
        }
      } else {
        toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const renderSubAreasEYFS = (el: any) => {
    return (
      <div className={`pd-y-15 mx-n-20 pd-x-20 border-bottom border-gray border-b-w-6`}>
        <div className='cursor-pointer' onClick={() => {
          if (isVisible.includes(el?.name)) {
            const filteredVisible = isVisible.filter((item) => item !== el?.name);
            setIsVisible(filteredVisible)
          } else {
            setIsVisible([
              ...isVisible,
              el?.name
            ])
          }
        }}>
          <div className="d-flex align-items-center cursor-pointer">
            <div className='visible-in-print-view'><IoCaretDownSharp size={15} /></div>
            <div className='print-element-hide'>{isVisible.includes(el?.name) ? <IoCaretDownSharp size={15} /> : <IoCaretForwardSharp size={15} />}</div>
            <div className='fs-14 fw-semibold text-black mg-l-10 flex-1'>
              {el?.name}
            </div>
          </div>
        </div>
        <div className='visible-in-print-view'>
          {el?.aspects && el?.aspects.map((asp: any,) => {
          return (
            <>
              <div className="pd-l-33 mg-t-15">
                <p className='fs-12 fw-semibold text-black mg-b-0'>{asp?.name}</p>

                <div className="rounded-2 pd-y-6 pd-r-8 position-relative pd-l-22 mg-t-10 mg-l-10 " style={{ border: '1px solid ' + (asp?.onEntry?.color ? asp?.onEntry?.color : '#E9E9E9') }}>
                  <div className="dots rounded position-absolute" style={{ backgroundColor: asp?.onEntry?.color }}></div>
                  <div className="d-flex align-items-center">
                    <p className="fs-12 text-black mg-b-0 flex-1">{asp?.onEntry?.value || ''}</p>
                    <p className="fs-12 text-gray-400 mg-b-0 mg-l-10">on entry</p>
                  </div>
                </div>

                <div className="rounded-2 pd-y-6 pd-r-8 position-relative pd-l-22 mg-t-10 mg-l-10 " style={{ border: '1px solid ' + (asp?.onExit?.color ? asp?.onExit?.color : '#E9E9E9') }}>
                  <div className="dots rounded position-absolute" style={{ backgroundColor: asp?.onExit?.color }}></div>
                  <div className="d-flex align-items-center">
                    <p className="fs-12 text-black mg-b-0 flex-1">{asp?.onExit?.value || ''}</p>
                    <p className="fs-12 text-gray-400 mg-b-0 mg-l-10">on exit</p>
                  </div>
                </div>
              </div>
            </>

          )
        })}
        </div>
        <div className='print-element-hide'>{isVisible.includes(el?.name) && el?.aspects && el?.aspects.map((asp: any,) => {
          return (
            <>
              <div className="pd-l-33 mg-t-15">
                <p className='fs-12 fw-semibold text-black mg-b-0'>{asp?.name}</p>

                <div className="rounded-2 pd-y-6 pd-r-8 position-relative pd-l-22 mg-t-10 mg-l-10 " style={{ border: '1px solid ' + (asp?.onEntry?.color ? asp?.onEntry?.color : '#E9E9E9') }}>
                  <div className="dots rounded position-absolute" style={{ backgroundColor: asp?.onEntry?.color }}></div>
                  <div className="d-flex align-items-center">
                    <p className="fs-12 text-black mg-b-0 flex-1">{asp?.onEntry?.value || ''}</p>
                    <p className="fs-12 text-gray-400 mg-b-0 mg-l-10">on entry</p>
                  </div>
                </div>

                <div className="rounded-2 pd-y-6 pd-r-8 position-relative pd-l-22 mg-t-10 mg-l-10 " style={{ border: '1px solid ' + (asp?.onExit?.color ? asp?.onExit?.color : '#E9E9E9') }}>
                  <div className="dots rounded position-absolute" style={{ backgroundColor: asp?.onExit?.color }}></div>
                  <div className="d-flex align-items-center">
                    <p className="fs-12 text-black mg-b-0 flex-1">{asp?.onExit?.value || ''}</p>
                    <p className="fs-12 text-gray-400 mg-b-0 mg-l-10">on exit</p>
                  </div>
                </div>
              </div>
            </>

          )
        })}</div>


      </div>
    )
  };

  const convertToTitleCase = (input: any) => {
    return input
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (match: any) => match.toUpperCase())
      .trim();
  }

  const renderEYFSFrameworks = () => {

    const eyfsData = [];

    if (reportDetails?.frameworks?.eyfs) {
      for (const [key, value] of Object.entries(reportDetails?.frameworks?.eyfs)) {
        const data = <>
          <p className='fs-14 fw-semibold mg-t-15 text-black mg-b-0'>{convertToTitleCase(key)}</p>
          <div>
            {value && isArray(value) && value.map((el: any) => {
              return renderSubAreasEYFS(el)
            })}
          </div>
        </>

        eyfsData.push(data)
      }
    };

    if (eyfsData.length > 0) {
      return eyfsData
    }

    return <></>
  }

  if (loading) {
    return <TransitionReportLoader />
  }

  return (
    <ProfileLayout title={"Transition Report"} fixedFooter={reportDetails?.status === REPORT_STATUS.PENDING_PARENT_REVIEW ? true : false} isPrintPage={true} handlePrint={() => window.print()}>
      <>
        {/* {submitting ? <div>Full screen Loader...</div> : null} */}
        <div className="fs-12 text-gray-300 text-center">
          <span className="fw-bold">{reportDetails?.name}</span> - {reportDetails?.date ? dateFormatter(reportDetails?.date, DATE_FORMATS.SHORT_DATE) : ''}
        </div>
        <div className="fs-12 text-gray-300 text-center mg-t-5">
          {reportDetails?.childDetails.age} Months {reportDetails?.childDetails.roomName ? `- ${reportDetails?.childDetails.roomName}` : null}
        </div>

        {/* {reportDetails && reportDetails?.legends?.eyfs.length > 0 ? (
          <div className="mg-t-20">
            <LegendsELG
              data={reportDetails?.legends?.eyfs}
              title="Legend"
            />
          </div>
        ) : null} */}

        <div className='form-group mg-t-15'>
          <div className='form-label text-gray-300 mb-1 fs-12'>Child's Name</div>
          <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.childDetails?.name || ''}</div>
        </div>
        <div className="row gutter-8">
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Gender</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.childDetails?.gender || ''}</div>
            </div>
          </div>
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Date of Birth</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.childDetails?.dob ? dateFormatter(reportDetails?.childDetails?.dob, DATE_FORMATS.SHORT_DATE) : ''}</div>
            </div>
          </div>
        </div>
        <div className="row gutter-8">
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Main Language</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.childDetails?.mainLanguage || ''}</div>
            </div>
          </div>
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Other Languages</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.childDetails?.otherLanguages || ''}</div>
            </div>
          </div>
        </div>
        <div className="row gutter-8">
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Name of Setting</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.nurseryDetails?.name || ''}</div>
            </div>
          </div>
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Contact Details</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.nurseryDetails?.contact || ''}</div>
            </div>
          </div>
        </div>
        <div className='form-group mg-t-15'>
          <div className='form-label text-gray-300 mb-1 fs-12'>Number of terms</div>
          <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.reportDetails?.numberOfTerms || ''}</div>
        </div>
        <div className="row gutter-8">
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Date of Entry</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.reportDetails?.dateOfEntry ? dateFormatter(reportDetails?.reportDetails?.dateOfEntry, DATE_FORMATS.SHORT_DATE) : ''}</div>
            </div>
          </div>
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Date Left</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.reportDetails?.dateLeft ? dateFormatter(reportDetails?.reportDetails?.dateLeft, DATE_FORMATS.SHORT_DATE) : ''}</div>
            </div>
          </div>
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Type of Attendance</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.reportDetails?.typeOfAttendance || ''}</div>
            </div>
          </div>
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Attendance Comments</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.reportDetails?.attendanceComments || ''}</div>
            </div>
          </div>
          <div className="col-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Other Settings attended</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.reportDetails?.otherSettingsAttended || ''}</div>
            </div>
          </div>
        </div>

        {reportDetails?.frameworks?.eyfs && Object.keys(reportDetails?.frameworks?.eyfs).length > 0 && (
          <>
            <div className='fs-14 fw-semibold mg-t-20 text-black'>Early Years Foundation Stage Development Levels</div>
            {reportDetails && reportDetails?.legends?.eyfs.length > 0 ? (
              <div className="mg-t-20">
                <LegendsELG
                  data={reportDetails?.legends?.eyfs}
                  title="Legend"
                />
              </div>
            ) : null}
            
            {renderEYFSFrameworks()}
          </>
        )}

        <div className="mx-n-20 pd-x-20 pd-b-15">
          <div className='form-group mg-t-15'>
            <div className='form-label text-gray-300 mb-1 fs-12'>Date the 2 year progress check was completed</div>
            <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.progressCheck ? dateFormatter(reportDetails?.progressCheck, DATE_FORMATS.SHORT_DATE) : ''}</div>
          </div>
          <div className='form-group mg-t-15'>
            <div className='form-label text-gray-300 mb-1 fs-12'>Any issues identified e.g SEN, medical Condition</div>
            <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.issuesIdentified || ''}</div>
          </div>
          <div className='form-group mg-t-15'>
            <div className='form-label text-gray-300 mb-1 fs-12'>Referrals made and/ or outside agencies involved</div>
            <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.referralsInvolved ? 'Yes' : 'No'}</div>
          </div>
        </div>

        {reportDetails && (reportDetails?.frameworks?.coel?.areas?.length > 0 || reportDetails?.legends?.elg.length > 0) ? (
          <div className={`pd-y-15 mx-n-20 pd-x-20 border-top border-gray border-t-w-6`}>

            {reportDetails && reportDetails?.legends?.elg.length > 0 ? (
              <div className="pd-b-15">
                <LegendsELG
                  data={reportDetails?.legends?.elg}
                  title="Legend (Early Learning Goals)"
                />
              </div>
            ) : null}

            {reportDetails?.frameworks?.coel?.areas?.length > 0 ? <COEL
              data={reportDetails?.frameworks?.coel?.areas}
              currTabDetailsShown={isVisible}
              onPressTab={(isVisibleArray: Array<string>) => setIsVisible(isVisibleArray)}
              title="Characteristics of Effective Learning:"
            /> : null}

          </div>
        ) : null}

        {reportDetails?.mySelfHelpSkill && reportDetails?.mySelfHelpSkill?.length > 0 && (
          <div className="mx-n-20 pd-x-20 pd-b-15 border-top border-gray border-t-w-6">
            <div className='fs-14 fw-semibold mg-t-15 text-black'>My Self Help Skill</div>

            {reportDetails?.mySelfHelpSkill?.map((el: any) => {
              return (
                <div className="mg-t-15">
                  <div className='fs-12 fw-semibold text-black'>{el?.label || ''}</div>
                  <div className="fs-12 text-black mg-t-10">{el?.value || ''}</div>
                  <p className="fs-12 text-black mg-t-10"><TextRenderer text={el?.notes || ''} /></p>
                </div>
              )
            })}
          </div>
        )}
        {reportDetails && reportDetails?.fields.length > 0 ? (
          <ReportFields
            data={reportDetails?.fields}
          />
        ) : null}

        {reportDetails?.parentComments?.visible && REPORT_STATUS.PENDING_PARENT_REVIEW !== reportDetails?.status && (
          <div className="mx-n-20 pd-x-20 pd-b-15 border-top border-gray border-t-w-6">
            <div className='form-group mg-t-15'>
              <div className='form-label text-black fw-semibold mb-1 fs-12'>Parent(s) Comments</div>

              <BaseCard customBaseCardStyle="min-h100 mg-t-10 rounded-2 pd-15"><p className="mg-b-0 form-control read-only-control d-flex align-items-center">{reportDetails?.parentComments?.value || ''}</p></BaseCard>

              {/* {REPORT_STATUS.PENDING_PARENT_REVIEW === reportDetails?.status && (
                  <input type="text" placeholder="Type here" value={parentComments} onChange={(e) => setParentComments(e.target.value)}/>
                )} */}

              <p className="fs-12 text-black fw-semibold mg-t-10">My parent has my learning journey which I would love to share with you. Please ask to see.</p>

            </div>

            {reportDetails?.parentComments?.permissions.length > 0 && <div className="mg-t-20">
              {reportDetails?.parentComments?.permissions.map((el: any, index) => {
                return (
                  <div className={`${index === 0 ? '' : 'mg-t-15'}`}>
                    <div className="d-flex align-items-center">
                      {/* <CustomCheckbox isChecked={el?.value} customIconStyle={{ width: '16px', height: '16px' }} /> */}
                      {el?.value ? <div className='checked-circle mg-r-5 rounded-circle bg-gray-200 d-flex align-items-center justify-content-center'><MdCheck size={12} /></div> : <div className='checked-circle mg-r-5 rounded-circle border border-gray-900'></div>}
                      <p className="fs-12 text-black mg-b-0 mg-l-5">{el?.label || ''}</p>
                    </div>
                  </div>
                )
              })
              }

              {/* {(REPORT_STATUS.PENDING_PARENT_REVIEW === reportDetails?.status) && (reportDetails?.parentComments?.permissions.length > 0) && (
                  <>
                    <div className="mg-t-15">
                      <CustomCheckbox
                        isChecked={keepCopy}
                        onChange={() => { setKeepCopy(val => !val) }}
                        label={reportDetails?.parentComments?.permissions[0]?.label || ''}
                        customCheckboxLabelStyle="fs-12"
                        customIconStyle={{ width: '16px', height: '16px' }}
                      />
                    </div>

                    <div className="mg-t-12">
                      <CustomCheckbox
                        isChecked={shareWithOther}
                        onChange={() => { setShareWithOther(val => !val) }}
                        label={reportDetails?.parentComments?.permissions[1]?.label || ''}
                        customCheckboxLabelStyle="fs-12"
                        customIconStyle={{ width: '16px', height: '16px' }}
                      />
                    </div>
                  </>
                )} */}
            </div>}
          </div>
        )}

        <div className="mx-n-20 pd-x-20 border-top border-gray border-t-w-6">
          {reportDetails?.status !== REPORT_STATUS.PENDING_PARENT_REVIEW ? <div className='form-group mg-t-15'>
            <div className='form-label text-gray-300 mb-1 fs-12'>Parent(s) / Carer(s) Name</div>
            <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.signatures?.parent?.value || ''}</div>
          </div> : null
          }
          {reportDetails?.status !== REPORT_STATUS.PENDING_PARENT_REVIEW ? <div className='form-group mg-t-15'>
            <div className='form-label text-gray-300 mb-1 fs-12'>Date</div>
            <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.signatures?.parent?.timestamp ? dateFormatter(reportDetails?.signatures?.parent?.timestamp, DATE_FORMATS.LONG_DATE_WITH_TIME) : ''}</div>
          </div> : null}

          <div className='border border-gray-200 rounded-3 pd-15 mg-t-15'>
            <div className='form-group'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Staff Name</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.signatures?.staff?.value || ''}</div>
            </div>

            <div className='form-group mg-t-15'>
              <div className='form-label text-gray-300 mb-1 fs-12'>Date</div>
              <div className='form-control read-only-control d-flex align-items-center'>{reportDetails?.signatures?.staff?.timestamp ? dateFormatter(reportDetails?.signatures?.staff?.timestamp, DATE_FORMATS.SHORT_DATE) : ''}</div>
            </div>
          </div>
          {reportDetails?.status === REPORT_STATUS.PENDING_PARENT_REVIEW && (
            <>
              {/* <div className='form-group mg-t-15'>
                  <div className='form-label text-gray-300 mg-b-10 fs-12'>Parent Acknowledgement:</div>
                  <CustomCheckbox
                    isChecked={acknowledged}
                    onChange={() => setIsAcknowledged(val => !val)}
                    label={"I have read the report."}
                    customCheckboxLabelStyle="fs-12"
                    customIconStyle={{ width: '16px', height: '16px' }}
                  />
                </div> */}

              <div className="section-fixed pd-b-10 pd-t-10 pd-x-20 print-element-hide">
                <CustomButton
                  text={"Submit to Complete"}
                  // onClick={onPressComplete}
                  onClick={navigateToDownloadAppScreen}
                  color={"primary"}
                  isLoading={submitting}
                />
              </div>
            </>
          )}
        </div>
      </>
    </ProfileLayout>
  )
}

export default TransitionReport;